import { ClipboardCheck, ClipboardList, UserCheck } from "lucide-react";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { useCommunicationHubQuery } from "@/modules/communication/presentation/api/queries";
import { useLineManagerLeaveStatusQuery } from "@/modules/hr/presentation/api/queries";
import { usePerformanceHubQuery } from "@/modules/performance-tracking/presentation/api/queries";
import { useQcInspectorStatusQuery } from "@/modules/qc/presentation/api/queries";
import { useTrainingHubQuery } from "@/modules/training-standard/presentation/api/queries";
import { mergeNavGroupsWithBadges } from "./sidebarNavBadges";
import type { NavGroup } from "./types";

type SidebarNavAlertsContextValue = {
  navGroupsWithBadges: NavGroup[];
  /** ລວມທຸກປະເພດແຈ້ງເຕືອນທີ່ນັບໃນ sidebar (checklist + ວຽກມອບໝາຍ + communication unread, …) */
  totalBadgeCount: number;
};

export const SidebarNavAlertsContext =
  createContext<SidebarNavAlertsContextValue | null>(null);

export function SidebarNavAlertsProvider({
  enabled,
  baseNavGroups,
  children,
}: {
  enabled: boolean;
  baseNavGroups: NavGroup[];
  children: ReactNode;
}) {
  const { user } = useAuthState();
  const trainingHub = useTrainingHubQuery({ enabled });
  const performanceHub = usePerformanceHubQuery({ enabled });
  const commHub = useCommunicationHubQuery({ enabled });
  const qcInspector = useQcInspectorStatusQuery({
    enabled: enabled && Boolean(user?.id),
  });
  const lineManagerLeaveHub = useLineManagerLeaveStatusQuery(
    Boolean(enabled && user?.id),
  );

  const lineManagerPendingLeave =
    lineManagerLeaveHub.data?.pendingLineCount ?? 0;

  const trainingAlertTotal = useMemo(() => {
    const a = trainingHub.data?.checklistAlerts;
    if (!a) return 0;
    return a.overdue + a.dueSoon;
  }, [trainingHub.data]);

  const performanceTaskAlertTotal = useMemo(() => {
    const a = performanceHub.data?.taskAlerts;
    if (!a) return 0;
    return a.overdue + a.dueSoon;
  }, [performanceHub.data]);

  const commUnread = commHub.data?.stats.unreadNotifications ?? 0;

  const leafCounts = useMemo(
    () =>
      ({
        "/app/training/checklists": trainingAlertTotal,
        "/app/performance/kpi": performanceTaskAlertTotal,
        "/app/performance/tasks": performanceTaskAlertTotal,
        "/app/communication/notifications": commUnread,
        "/app/hr/leave/line-manager": lineManagerPendingLeave,
      }) satisfies Record<string, number>,
    [
      trainingAlertTotal,
      performanceTaskAlertTotal,
      commUnread,
      lineManagerPendingLeave,
    ],
  );

  const sectionTotals = useMemo(
    () => ({
      training: trainingAlertTotal,
      communication: commUnread,
      hr: 0,
      documents: 0,
    }),
    [trainingAlertTotal, commUnread],
  );

  const totalBadgeCount =
    trainingAlertTotal +
    performanceTaskAlertTotal +
    commUnread +
    lineManagerPendingLeave;

  const navGroupsWithBadges = useMemo(() => {
    const merged = mergeNavGroupsWithBadges(
      baseNavGroups,
      leafCounts,
      sectionTotals,
    );
    const qcItems: NavGroup["items"] = [];
    if (qcInspector.data?.isInspector === true) {
      qcItems.push({
        title: "ກວດສອບ QC",
        url: "/app/qc/inspection",
        icon: ClipboardCheck,
      });
    }
    qcItems.push({
      title: "ບັນທຶກກວດພາຍໃນ (ຕຳແໜ່ງ)",
      url: "/app/internal-audit",
      icon: ClipboardList,
    });
    const qcGroup: NavGroup = {
      title: "ກວດສອບ QC",
      items: qcItems,
    };
    let hrAugmented = [...merged, qcGroup];
    if (lineManagerLeaveHub.data?.isLineApprover === true) {
      hrAugmented = hrAugmented.map((g) => {
        if (g.title !== "ບໍລິການ HR") return g;
        const itemsIn = [...g.items];
        const idx = itemsIn.findIndex(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            "url" in item &&
            item.url === "/app/hr/leave/requests",
        );
        if (idx === -1) return g;
        const nextItems = [...itemsIn];
        nextItems.splice(idx + 1, 0, {
          title: "ອະນຸມັດລາ (ທີມ)",
          url: "/app/hr/leave/line-manager",
          icon: UserCheck,
        });
        return { ...g, items: nextItems };
      });
    }
    return hrAugmented;
  }, [
    baseNavGroups,
    leafCounts,
    sectionTotals,
    qcInspector.data?.isInspector,
    lineManagerLeaveHub.data?.isLineApprover,
  ]);

  const value = useMemo(
    () => ({ navGroupsWithBadges, totalBadgeCount }),
    [navGroupsWithBadges, totalBadgeCount],
  );

  return (
    <SidebarNavAlertsContext.Provider value={value}>
      {children}
    </SidebarNavAlertsContext.Provider>
  );
}

/** ສຳລັບ Header / SidebarTrigger — ຖ້າບໍ່ຢູ່ໃນ Provider ໃຫ້ຄືນ 0 */
export function useSidebarTotalBadgeCount(): number {
  return useContext(SidebarNavAlertsContext)?.totalBadgeCount ?? 0;
}

export function useSidebarNavGroupsWithBadges(): NavGroup[] {
  const ctx = useContext(SidebarNavAlertsContext);
  if (!ctx) {
    throw new Error(
      "useSidebarNavGroupsWithBadges must be used within SidebarNavAlertsProvider",
    );
  }
  return ctx.navGroupsWithBadges;
}
